import { useState } from 'react';

import { ISSUE_PRIORITIES, ISSUE_STATUSES } from '../../shared/schema.ts';
import type { IssuePriority, IssueStatus } from '../../shared/schema.ts';
import { mutate, useUser } from '../api.ts';
import { useLive } from '../live.ts';
import type { Project, User } from '../types.ts';
import { PriorityLabel, StatusTag } from './Marks.tsx';
import { Picker } from './Picker.tsx';

/** The create form. One write — the issue, its description and its tags in one transaction — and
 *  then the pane opens the new issue, which is already live for everyone else. */
export function CreateIssue({ project, projects, onCancel, onCreated }: { project: string | null; projects: Project[]; onCancel: () => void; onCreated: (id: string) => void }) {
  const user = useUser();
  const users = useLive<User[]>('users').data ?? [];
  // Defaults to whatever the list was showing, and is changeable here, because creating an issue
  // in the project you were looking at is right nearly always and wrong often enough to matter.
  const [into, setInto] = useState(project ?? '');
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<IssueStatus>('todo');
  const [priority, setPriority] = useState<IssuePriority>('medium');
  const [owner, setOwner] = useState(user);
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const owners = users.some(u => u.id === user) ? users : [{ id: user, name: `${user} (you)` }, ...users];

  return (
    <div className="detail-inner">
      <header className="pane-head">
        <span className="eyebrow">NEW ISSUE</span>
        <button type="button" className="close" aria-label="Close" onClick={onCancel}>×</button>
      </header>
      <form className="create" onSubmit={async e => {
        e.preventDefault();
        setBusy(true);
        const created = await mutate<{ id: string }>('createIssue', {
          project: into, title, status, priority, owner, description,
          tags: tags.split(/[,\s]+/).map(t => t.trim()).filter(Boolean),
        });
        setBusy(false);
        if (created?.id) onCreated(created.id);
      }}>
        <label className="field"><span className="micro">PROJECT</span>
          <select value={into} onChange={e => setInto(e.target.value)} disabled={!projects.length} required>
            {!projects.length && <option value="">Loading…</option>}
            {projects.map(p => <option key={p.id} value={p.id}>{p.key} · {p.name}</option>)}
          </select>
        </label>
        <label className="field"><span className="micro">TITLE</span>
          <input autoFocus required maxLength={180} value={title} onChange={e => setTitle(e.target.value)} placeholder="What is wrong, in a sentence" />
        </label>
        <div className="field-row">
          <div className="field"><span className="micro">STATUS</span>
            <Picker label="Status" value={status} options={ISSUE_STATUSES} render={(s: IssueStatus) => <StatusTag status={s} />} onChange={setStatus} />
          </div>
          <div className="field"><span className="micro">PRIORITY</span>
            <Picker label="Priority" value={priority} options={ISSUE_PRIORITIES} render={(p: IssuePriority) => <PriorityLabel priority={p} />} onChange={setPriority} />
          </div>
        </div>
        <label className="field"><span className="micro">OWNER</span>
          <select value={owner} onChange={e => setOwner(e.target.value)}>{owners.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
        </label>
        <label className="field"><span className="micro">TAGS</span>
          <input value={tags} onChange={e => setTags(e.target.value)} placeholder="sync, ux" />
          <small>Up to eight, separated by commas.</small>
        </label>
        <label className="field"><span className="micro">DESCRIPTION</span>
          <textarea rows={5} maxLength={2000} value={description} onChange={e => setDescription(e.target.value)} placeholder="Steps, what you expected, what happened." />
          <small>Try a title with the word “spam” in it to watch the server refuse it.</small>
        </label>
        <div className="row-actions">
          <button type="submit" className="button" disabled={busy || !title.trim() || !into}>Create issue <span aria-hidden="true">→</span></button>
          <button type="button" className="link" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
